import { Button, Form, Input } from "antd";
import { Rule } from "antd/es/form";
import { DeleteOutlined } from "@ant-design/icons";
import { ComponentProps, useRef, useState } from "react";

type ListRules = ComponentProps<typeof Form.List>['rules'];
import ColorTrigger from "./ColorTrigger";
import "./CustomLinksFormList.less";
import { API_URL } from "src/data/Api";
import useGetApiData from "src/hook/useGetApiData";
import { CustomLinkColor, CustomLinkColorsResponseDTO, UpdateCustomLinkColorDTO } from "@dto/customLinks.dto";
import { postApi } from "src/util/apiCalls";
import { AxiosError } from "axios";
import { Log } from "src/service/log.service";
import { THEMES } from "src/styles/variables";
import { ThemeUtil } from "src/util/ThemeUtil";
import { normalizedUrl } from "./CustomLink";

export interface CustomLinkEntry {
  label: string;
  url: string;
  color: string;
  newTab: boolean;
}

export const makeEmptyLink = (color: string = THEMES[ThemeUtil.getThemeName()].xmVars['xm-color-secondary'] ?? '#1677ff') =>
  ({ label: undefined, url: undefined, color, newTab: true });

const isSafeLinkUrl = (url: string) => {
  try {
    const { protocol } = new URL(normalizedUrl(url));
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

interface CustomLinksFormListProps {
  name?: string;
  listRules?: ListRules;
  required?: boolean;
}

const CustomLinksFormList = ({
  name = 'custom_links',
  listRules,
  required = true,
}: CustomLinksFormListProps) => {
  const form = Form.useFormInstance();
  const listRef = useRef<HTMLDivElement>(null);
  const initial = useRef<ReturnType<typeof makeEmptyLink>[]>();
  if (!initial.current)
    initial.current = [makeEmptyLink()];

  const defaultValue = initial.current;
  const [wasColorPickerOpened, setWasColorPickerOpened] = useState(false);
  const [colors, setColors, loading] = useGetApiData<CustomLinkColor[], CustomLinkColorsResponseDTO>(
    `${API_URL.CONFIGURATION}/custom-links/colors`,
    `Failed to load colors for custom links!`,
    [wasColorPickerOpened],
    [],
    wasColorPickerOpened ? 'YES' : 'NO'
  );

  const handleChangeColors = (colors: UpdateCustomLinkColorDTO[]) =>
    postApi<CustomLinkColorsResponseDTO, UpdateCustomLinkColorDTO[]>(`${API_URL.CONFIGURATION}/custom-links/colors`, colors).then(
      ({ data: { data } }) => setColors(data),
      (reason: AxiosError) => Log.error('Failed to update colors!', reason)
    );

  const fieldRules = (message: string): Rule[] => [{ required: true, message }];

  const handleOpenChange = () => {
    if (wasColorPickerOpened)
      return;

    setWasColorPickerOpened(true);
  };

  const handleAdd = (add: (defaultValue: ReturnType<typeof makeEmptyLink>, insertIndex: number) => void) => {
    add(makeEmptyLink(), 0);
    requestAnimationFrame(() => listRef.current?.querySelector<HTMLInputElement>('.xm-link-input')?.focus());
  };

  return (
    <Form.List name={name} rules={listRules} initialValue={defaultValue}>
      {(fields, { add, remove }, { errors }) => (
        <div className="xm-link-form-list" style={{ display: 'flex', flexDirection: 'column', gap: '1em' }}>
          <div ref={listRef} className="xm-link-list" style={{ display: 'flex', flexDirection: 'column', gap: '.5em' }}>
            {fields.map(({ key, name: fieldName, ...rest }) => (
              <Form.Item key={key} noStyle shouldUpdate>
                {() => {
                  const hasError = form.getFieldError([name, fieldName, 'label']).length > 0
                    || form.getFieldError([name, fieldName, 'url']).length > 0;
                  return (
                    <div className={hasError ? 'xm-link-box xm-error' : 'xm-link-box'}>
                      <Form.Item {...rest} noStyle name={[fieldName, 'label']} dependencies={[[name, fieldName, 'url']]} rules={fieldRules('Enter label')}>
                        <Input
                          variant="borderless"
                          placeholder="Enter label"
                          className="xm-link-input"
                          style={{ gridColumn: 1, gridRow: 1, borderRadius: 0 }}
                        />
                      </Form.Item>
                      <Form.Item {...rest} noStyle name={[fieldName, 'url']} dependencies={[[name, fieldName, 'label']]} rules={[
                        ...fieldRules('Enter URL'),
                        { validator: (_, value: string) => !value || isSafeLinkUrl(value) ? Promise.resolve() : Promise.reject(new Error('URL must start with http:// or https://')) },
                      ]}>
                        <Input
                          variant="borderless"
                          placeholder="Enter URL"
                          className="xm-link-input xm-sep-left"
                          style={{ gridColumn: 2, gridRow: 1, borderRadius: 0 }}
                        />
                      </Form.Item>
                      <div className="xm-link-color-cell xm-sep-left" style={{
                        gridColumn: 3,
                        display: 'grid',
                      }}>
                        <Form.Item {...rest} noStyle name={[fieldName, 'color']} getValueFromEvent={(_, hex) => hex}>
                          <ColorTrigger
                            colors={colors}
                            loading={loading}
                            onChangeColors={handleChangeColors}
                            onOpenChange={handleOpenChange}
                          />
                        </Form.Item>
                      </div>
                      <Button
                        type="text"
                        danger
                        size="small"
                        className="xm-link-delete xm-sep-left"
                        icon={<DeleteOutlined />}
                        disabled={fields.length === 1 && required}
                        onClick={() => remove(fieldName)}
                        style={{ gridColumn: 4, height: '100%', borderRadius: 0 }}
                      />
                    </div>
                  );
                }}
              </Form.Item>
            ))}
          </div>
          <Button
            block
            onClick={() => handleAdd(add)}
          >
            Add link
          </Button>
          <Form.ErrorList errors={errors} />
        </div>
      )}
    </Form.List>
  );
};

export default CustomLinksFormList;
